import React, { useEffect } from 'react'
import { ScrollView, View } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Button, Pill, Text } from '@components/base'
import { StyleService, useStyleSheet } from '@style/service.ts'
import { LinearGradient } from '@components'
import { ReferralCouponKind, UiPillFill, UiStateNames } from '@src/types.ts'
import { CurvyArrow } from '@assets/svgs/curvyArrow.tsx'
import { referralConfigSelector } from '@selectors/app.ts'
import { useGoBack } from '@utils'
import { AppStackParamList } from '@navigation/types'
import { Analytics, CustomEventTypes } from '@config'

const RIBBON_TEXT =
  ' A FRIEND   •   REFER A FRIEND   •   REFER A FRIEND   •   REFER A FRIEND   •   REFER A FRIEND'

interface ReferralPopupProps {
  onDismiss?: () => void
}

export const ReferralPopup = (props: ReferralPopupProps) => {
  const { onDismiss: onDismissProp } = props
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const dispatch = useDispatch()
  const goBack = useGoBack()
  const insets = useSafeAreaInsets()
  const theme = useTheme()
  const gradientColor = theme['theme.solid.black']
  const { credits, discount, discountPercentage, kind } = useSelector(referralConfigSelector)
  const discountPercentageRounded = discountPercentage ? Math.floor(discountPercentage) : null

  useEffect(() => {
    Analytics.track(CustomEventTypes.ReferralPopupViewed)
  }, [])

  const markDismissed = () => {
    dispatch({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.ReferralPopupDismissed,
        value: true,
      },
    })
  }

  const onRefer = () => {
    markDismissed()
    Analytics.track(CustomEventTypes.ReferralPopupClicked)
    if (onDismissProp) {
      navigation.navigate('FriendReferral', { source: 'UpsellOffer' })
      return
    }

    navigation.replace('FriendReferral', { source: 'Drawer' })
  }

  const onDismiss = () => {
    markDismissed()
    Analytics.track(CustomEventTypes.ReferralPopupDismissed)

    if (onDismissProp) {
      onDismissProp()
      return
    }

    goBack()
  }

  const discountText = discountPercentageRounded ? `${discountPercentageRounded}%` : `$${discount}`
  const isAmazon = kind === ReferralCouponKind.AmazonGiftCard

  return (
    <ScrollView
      style={styles.scrollView}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.content}
    >
      <View style={styles.bannerContainer}>
        <View>
          <View style={[styles.whiteRibbon, styles.firstRibbon]}>
            <Text type="tiny" bold numberOfLines={1} ellipsizeMode="clip" style={styles.text}>
              {RIBBON_TEXT}
            </Text>
          </View>
          <View style={[styles.yellowRibbon, styles.secondRibbon]}>
            <Text type="tiny" bold numberOfLines={1} ellipsizeMode="clip" style={styles.darkText}>
              {RIBBON_TEXT}
            </Text>
          </View>
        </View>
        <View style={styles.bannerTextContainer}>
          <Text type="title-2" style={styles.text}>
            GIVE {discountText} OFF
          </Text>
          <View style={styles.creditsContainer}>
            <Text type="title-1" style={styles.yellowText}>
              GET A ${credits}
              {isAmazon && ' GIFT CARD'}
            </Text>
            {isAmazon && (
              <>
                <Pill
                  text="To Amazon"
                  textType="regular"
                  fill={UiPillFill.White}
                  style={styles.pill}
                />
                <CurvyArrow style={styles.curvyArrow} />
              </>
            )}
          </View>
        </View>
        <View>
          <View style={[styles.whiteRibbon, styles.thirdRibbon]}>
            <Text type="tiny" bold numberOfLines={1} ellipsizeMode="clip" style={styles.text}>
              {RIBBON_TEXT}
            </Text>
          </View>
          <View style={[styles.yellowRibbon, styles.fourthRibbon]}>
            <Text type="tiny" bold numberOfLines={1} ellipsizeMode="clip" style={styles.darkText}>
              {RIBBON_TEXT}
            </Text>
          </View>
          <LinearGradient
            start={{ x: 0, y: 1 }}
            end={{ x: 0, y: 0 }}
            startColor={gradientColor}
            endColor={gradientColor}
            startOpacity={1}
            endOpacity={0.6}
            style={styles.gradient}
          />
        </View>
      </View>
      <View style={styles.contentContainer}>
        <Text type="large" style={styles.text}>
          Get rewarded by having your friends and family take control of their health.
        </Text>
        <Text type="large" style={styles.text}>
          Give them {discountText} off their Nutrisense plan, and get a ${credits}{' '}
          {isAmazon && 'Amazon gift card '}for every successful referral.
        </Text>
        <Text type="large" style={styles.text}>
          There’s no limit — the more friends you refer, the more rewards you earn.
        </Text>
        <View style={[styles.buttonsContainer, { marginBottom: Math.max(insets.bottom, 16) }]}>
          <Button type="primary" size="block" onPress={onRefer} accessibilityLabel="Refer a Friend">
            Refer a Friend
          </Button>
          <Button
            type="transparent"
            size="block"
            textStyle={styles.text}
            onPress={onDismiss}
            accessibilityLabel="Dismiss"
          >
            Dismiss
          </Button>
        </View>
      </View>
    </ScrollView>
  )
}

const themedStyles = StyleService.create({
  scrollView: {
    backgroundColor: 'theme.solid.black',
  },
  content: {
    flex: 1,
    color: 'theme.solid.white',
    justifyContent: 'center',
  },
  contentContainer: {
    backgroundColor: 'theme.solid.black',
    paddingTop: 16,
    paddingHorizontal: 16,
    gap: 24,
  },
  buttonsContainer: {
    gap: 16,
  },
  text: {
    color: 'theme.solid.white',
  },
  darkText: {
    color: 'theme.solid.black',
  },
  yellowText: {
    color: 'theme.marketing.yellow',
  },
  bannerContainer: {
    justifyContent: 'space-evenly',
    flex: 1,
    marginBottom: -20,
  },
  bannerTextContainer: {
    paddingHorizontal: 16,
  },
  whiteRibbon: {
    position: 'relative',
    borderColor: 'theme.solid.white',
    borderTopWidth: 2,
    borderBottomWidth: 2,
    paddingVertical: 5,
    marginLeft: -10,
  },
  yellowRibbon: {
    position: 'relative',
    backgroundColor: 'theme.marketing.yellow',
    paddingVertical: 5,
    marginHorizontal: -5,
  },
  firstRibbon: {
    top: 20,
    transform: [{ rotate: '-7deg' }],
  },
  secondRibbon: {
    top: -15,
    transform: [{ rotate: '8deg' }],
  },
  thirdRibbon: {
    top: 10,
    transform: [{ rotate: '9deg' }],
  },
  fourthRibbon: {
    top: -20,
    transform: [{ rotate: '-8deg' }],
  },
  gradient: {
    height: 100,
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '100%',
    marginBottom: -20,
  },
  pill: {
    width: 110,
    marginTop: 8,
  },
  curvyArrow: {
    position: 'absolute',
    right: -35,
    top: 20,
  },
  creditsContainer: {
    alignSelf: 'flex-start',
    alignItems: 'flex-end',
  },
})
